import * as THREE from 'three';
import { WeatherType } from '../app/store';

export class WeatherSystem {
  public root: THREE.Group = new THREE.Group();
  private currentWeather: WeatherType = 'sunny';
  private rainPoints!: THREE.Points;
  private snowPoints!: THREE.Points;
  private rainGeometry!: THREE.BufferGeometry;
  private snowGeometry!: THREE.BufferGeometry;
  private rainPositions!: Float32Array;
  private snowPositions!: Float32Array;
  private snowVelocities!: Float32Array;

  private rainCount: number = 1800;
  private snowCount: number = 1400;
  private boundSize: number = 90;
  private reduceMotion: boolean = false;

  constructor() {
    this.root.name = 'WeatherSystem';
    this.createRainSystem();
    this.createSnowSystem();
    this.setWeather('sunny');
  }

  private createRainSystem(): void {
    this.rainPositions = new Float32Array(this.rainCount * 3);
    for (let i = 0; i < this.rainCount; i++) {
      this.rainPositions[i * 3] = (Math.random() - 0.5) * this.boundSize * 2;
      this.rainPositions[i * 3 + 1] = Math.random() * this.boundSize;
      this.rainPositions[i * 3 + 2] = (Math.random() - 0.5) * this.boundSize * 2;
    }

    this.rainGeometry = new THREE.BufferGeometry();
    this.rainGeometry.setAttribute('position', new THREE.BufferAttribute(this.rainPositions, 3));

    const mat = new THREE.PointsMaterial({
      color: 0x93c5fd,
      size: 1.4,
      transparent: true,
      opacity: 0.65,
      depthWrite: false,
    });

    this.rainPoints = new THREE.Points(this.rainGeometry, mat);
    this.rainPoints.visible = false;
    this.root.add(this.rainPoints);
  }

  private createSnowSystem(): void {
    this.snowPositions = new Float32Array(this.snowCount * 3);
    this.snowVelocities = new Float32Array(this.snowCount * 3);

    for (let i = 0; i < this.snowCount; i++) {
      this.snowPositions[i * 3] = (Math.random() - 0.5) * this.boundSize * 2;
      this.snowPositions[i * 3 + 1] = Math.random() * this.boundSize;
      this.snowPositions[i * 3 + 2] = (Math.random() - 0.5) * this.boundSize * 2;

      this.snowVelocities[i * 3] = (Math.random() - 0.5) * 1.5; // drift
      this.snowVelocities[i * 3 + 1] = 2.5 + Math.random() * 3.5; // fall speed
      this.snowVelocities[i * 3 + 2] = (Math.random() - 0.5) * 1.5;
    }

    this.snowGeometry = new THREE.BufferGeometry();
    this.snowGeometry.setAttribute('position', new THREE.BufferAttribute(this.snowPositions, 3));

    const mat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2.2,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    });

    this.snowPoints = new THREE.Points(this.snowGeometry, mat);
    this.snowPoints.visible = false;
    this.root.add(this.snowPoints);
  }

  public setWeather(weather: WeatherType): void {
    this.currentWeather = weather;
    this.rainPoints.visible = weather === 'rain';
    this.snowPoints.visible = weather === 'snow';
  }

  public setReduceMotion(reduce: boolean): void {
    this.reduceMotion = reduce;
  }

  public update(dt: number, cameraPosition: THREE.Vector3): void {
    if (this.currentWeather === 'rain') {
      const pos = this.rainPositions;
      const speedMult = this.reduceMotion ? 0.5 : 1.0;
      const fallSpeed = 48.0 * dt * speedMult;
      const drift = 12.0 * dt * speedMult;

      for (let i = 0; i < this.rainCount; i++) {
        pos[i * 3 + 1] -= fallSpeed;
        pos[i * 3] += drift * 0.4;

        // Wrap around camera bounding box
        if (pos[i * 3 + 1] < cameraPosition.y - 10) {
          pos[i * 3 + 1] = cameraPosition.y + this.boundSize;
          pos[i * 3] = cameraPosition.x + (Math.random() - 0.5) * this.boundSize * 1.5;
          pos[i * 3 + 2] = cameraPosition.z + (Math.random() - 0.5) * this.boundSize * 1.5;
        }
      }
      this.rainGeometry.attributes.position.needsUpdate = true;
    } else if (this.currentWeather === 'snow') {
      const pos = this.snowPositions;
      const vels = this.snowVelocities;
      const speedMult = this.reduceMotion ? 0.4 : 1.0;

      for (let i = 0; i < this.snowCount; i++) {
        pos[i * 3 + 1] -= vels[i * 3 + 1] * dt * speedMult;
        pos[i * 3] += Math.sin(pos[i * 3 + 1] * 0.1) * 1.5 * dt * speedMult;
        pos[i * 3 + 2] += Math.cos(pos[i * 3 + 1] * 0.1) * 1.5 * dt * speedMult;

        if (pos[i * 3 + 1] < cameraPosition.y - 10) {
          pos[i * 3 + 1] = cameraPosition.y + this.boundSize;
          pos[i * 3] = cameraPosition.x + (Math.random() - 0.5) * this.boundSize * 1.5;
          pos[i * 3 + 2] = cameraPosition.z + (Math.random() - 0.5) * this.boundSize * 1.5;
        }
      }
      this.snowGeometry.attributes.position.needsUpdate = true;
    }
  }

  public getWeather(): WeatherType {
    return this.currentWeather;
  }
}
